/* eslint-disable camelcase */
import { Controller } from 'stimulus';
import {
  sendApopheniaEvent,
  INTERACTION_TYPES,
  EVENT_TYPES,
  DEFAULT_ACTION,
} from '../src/js/logger';
import { ID_PATTERNS } from '../src/js/utils/logger_id_patterns';

class LoggerController extends Controller {
  connect() {
    this.originalScreenId = null;
    this.filteredScreenId = null;
    this.pathId = null;
    this._setScreenId();
  }

  /* eslint-disable-next-line lines-around-comment */
  /**
   * User interaction event to be logged. Element which triggers this event
   * SHOULD contain the following properties:
   * `data-logger-priority`
   * `id`
   * CAN contain additional properties via `data-logger-info` with the value of
   * a string which contains key=value pairs delineated by a semicolon.
   * e.g. `data-logger-info="status=follow_up;due_date=tomorrow"`.
   * @param {Event} event User interaction event
   * @returns {void}
   */
  logEvent(event) {
    const interaction_type = INTERACTION_TYPES[EVENT_TYPES[event.type]];
    const action = this.action;
    const priority = this.priority;
    const additionalData = this.additionalData;

    this._sendEvent({
      action,
      priority,
      interaction_type,
      element_id: this.element.id || null,
      additionalData,
    });
  }

  /**
   * Given enough information to formulate an apophenia log, invoke `sendApopheniaEvent`
   * passing all included params.
   * @param {Object} param Destructured param.
   * @param {String} param.action Action definition of user interaction.
   * @param {String} param.priority Priority type of log. One of: "INFO" | "WARN" | "ERROR".
   * @param {String} param.interaction_type Type of user interaction. e.g. 'CLICKED', 'FOCUSED', etc.
   * @param {String} param.element_id HTML ID of the element which was interacted with.
   * @param {Object} param.additionalData Optional information to be added as key/value pairs in `data` object.
   * @returns {void}
   */
  _sendEvent({
    action,
    priority,
    interaction_type = 'PAGE_VIEWED',
    element_id,
    additionalData = {},
  }) {
    sendApopheniaEvent({
      action,
      body: {
        data: {
          user_id: window.RT?.user_axid || null,
          screen_id: this.filteredScreenId,
          element_id,
          interaction_type,
          program_identifier: window.RT?.user_business_line || null,
          ...additionalData,
        },
      },
      priority,
    });
  }

  /**
   * Given a string of `[key]=[value]` pairs, delineated by a semicolon `;`,
   * separate the string into an object.
   * Consider the following example:
   * ```
   * input: "element_type=quick_filter;status=follow_up;due_date=tomorrow"
   *
   * output: {
   *   element_type: "quick_filter",
   *   status: "followup",
   *   due_date: "tomorrow",
   * }
   * ```
   * @param {String|undefined} info key=value pairs, semicolon delineated
   * @returns {Object|undefined} input parsed into an object
   */
  _parseInfo = (info) =>
    info
      ?.split(';')
      .filter((el) => el) // remove any empty strings (incase info ends in semicolon)
      .reduce((accum, curr) => {
        const [key, value] = curr.split('=');
        accum[key] = value;

        return accum;
      }, {});

  /**
   * Pass current `window.location.pathname` to `this._filterPath()` and set
   * the returned filteredScreenId to `this.filteredScreenId`, and set the returned
   * pathIds to `this.pathIds`.
   * @returns {void}
   */
  _setScreenId() {
    const currentPath = window.location.pathname
      .replace('/', '_')
      .replace(/^_/, '');
    const [filteredScreenId, pathIds] = this._filterPath(currentPath);

    this.originalScreenId = currentPath;
    this.filteredScreenId = filteredScreenId;
    this.pathIds = pathIds;
  }

  /**
   * Parse and filter current path.
   * If the current path includes any unique IDs (based on rules defined in
   * ../src/js/utils/logger_id_patterns.js) replace that section of the path with
   * `':id'`.
   * Returns an array with 2 elements. The first element is a String of the newly
   * filtered path. The second element is an array; either empty, or containing
   * all of the filtered IDs as strings.
   * @param {String} path current window.location.pathname, to be filtered
   * @returns {Array<String, Array<String>>} [filteredScreenId, [pathIds]]
   */
  _filterPath(path) {
    let filteredScreenId = path;
    let pathIds = [];
    const pathArr = path.split('/');

    for (let i = 0; i < pathArr.length; ++i) {
      const section = pathArr[i];

      if (ID_PATTERNS.hasOwnProperty(section)) {
        const patternRules = ID_PATTERNS[section];

        if (patternRules.pattern.test(pathArr[i + 1])) {
          const id = pathArr[i + 1];
          pathArr[i + 1] = patternRules.placeholder || ':id';

          filteredScreenId = pathArr.join('/');
          pathIds.push(id);
        }
      }
    }

    return [filteredScreenId, pathIds];
  }

  get action() {
    return this.data.get('action') || DEFAULT_ACTION;
  }

  get priority() {
    return this.data.get('priority') || 'INFO';
  }

  get additionalData() {
    const additionalData = {
      ...this._parseInfo(this.data.get('info')),
    };

    if (this.pathIds.length) {
      additionalData.path_ids = this.pathIds;
    }

    return additionalData;
  }
}

export default LoggerController;
/* eslint-enable camelcase */
